/**
 * FormatSmart — centralized format auto-detection, bitrate estimation, and UI binding.
 *
 * Works with ALL three UI patterns used across Timbrica tools:
 *   1. Buttons  (data-fmt):    .rv-fmt-btn, .kr-fmt, .vr-fmt, etc.
 *   2. Dropdown (data-format): .format-option (trim, volume, silence-remover, etc.)
 *   3. Select   (<select>):    #format-select, #ptExportFormat, etc.
 *
 * === ONE-LINE INTEGRATION ===
 *
 *   // After file is loaded and audio decoded:
 *   var rec = FormatSmart.autoApply(file, buffer.duration, {
 *       buttons: '.rv-fmt-btn',          // OR select: '#format-select'  OR dropdown: '.format-option'
 *       quality: '#rvQuality',           // optional: <select> for bitrate
 *       qualityRow: '#rvQualityRow',     // optional: container to show/hide
 *       formatData: FORMAT_DATA          // optional: from config('formats.output')
 *   });
 *   outputFormat = rec.format;           // apply to your variable
 *
 * === INDIVIDUAL HELPERS ===
 *
 *   FormatSmart.mapFormat('wma')                        → 'mp3'
 *   FormatSmart.mapFormat('wma', ['mp3','wav','ogg'])   → 'mp3'
 *   FormatSmart.estimateBitrate(file, 180)              → 256
 *   FormatSmart.closestBitrate(256, [128,192,256,320])  → 256
 *   FormatSmart.recommend(file, 180, ['mp3','wav'], FD) → {format:'mp3', bitrate:256}
 */
(function(){
'use strict';

// Map exotic/unsupported input formats → closest supported output format
var FORMAT_MAP = {
    wma:'mp3', amr:'mp3', awb:'mp3', ra:'mp3', mp2:'mp3',
    spx:'ogg', oga:'ogg', mka:'ogg',
    ape:'flac', tak:'flac', shn:'flac', tta:'flac', wv:'flac',
    alac:'m4a', m4r:'m4a', caf:'m4a',
    aiff:'wav', au:'wav', snd:'wav', voc:'wav',
    webm:'opus',
    '8svx':'wav', gsm:'mp3', mmf:'mp3'
};

function mapFormat(ext, available) {
    var mapped = FORMAT_MAP[ext] || ext;
    if (!available || !available.length) return mapped;
    for (var i = 0; i < available.length; i++) {
        if (available[i] === mapped) return mapped;
    }
    for (var j = 0; j < available.length; j++) {
        if (available[j] === ext) return ext;
    }
    return available[0];
}

/* ── A lossless recording inside a container that looks lossy ─────────────
   The default output follows the input EXTENSION, and `.m4a` / `.mov` / `.mp4`
   name a container, not a codec. iPhone Voice Memos in «Без потерь» mode writes
   ALAC into `.m4a`; cameras and field recorders write PCM into `.mov`. Mapped by
   extension, such a file defaulted to an AAC `.m4a`, so a lossless recording
   came back lossy and nothing on the page said so (support popova_katerina,
   23.09.2026, /denoise: 24-bit ALAC piano take → AAC). The codec sits in the
   header, and MediaAudioProbe reads it with a few small slices. */
var LOSSLESS_CODECS = { alac: 1, pcm: 1, flac: 1 };
var BOX_EXT = { m4a: 1, mp4: 1, mov: 1, m4b: 1, m4r: 1, alac: 1 };
var LOSSLESS_OUT = { wav: 1, flac: 1, aiff: 1, aif: 1, alac: 1 };
var _codecCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
var _probeLoad = null;

function _ensureMediaProbe() {
    if (window.MediaAudioProbe) return Promise.resolve(window.MediaAudioProbe);
    if (!_probeLoad) {
        _probeLoad = new Promise(function (resolve) {
            var settled = false;
            var done = function () {
                if (settled) return; settled = true;
                // A load that failed or is still crawling is NOT an answer: forget
                // it, so the next file asks again instead of inheriting «no probe»
                // for the rest of the page's life.
                if (!window.MediaAudioProbe) _probeLoad = null;
                resolve(window.MediaAudioProbe || null);
            };
            try {
                var s = document.createElement('script');
                s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/media-audio-probe.js') : '/js/media-audio-probe.js';
                s.async = true;
                s.onload = done; s.onerror = done;
                document.head.appendChild(s);
            } catch (_) { done(); }
            setTimeout(done, 30000);
        });
    }
    return _probeLoad;
}

/** Audio codec of an mp4-family file ('alac', 'aac', 'pcm', …) or '' when unknown. */
function sourceCodec(file) {
    if (!file) return Promise.resolve('');
    if (_codecCache && _codecCache.has(file)) return _codecCache.get(file);
    var p = _ensureMediaProbe().then(function (M) {
        if (!M || typeof M.probe !== 'function') {
            if (_codecCache) _codecCache.delete(file);   // no probe ran: not an answer to remember
            return '';
        }
        return Promise.resolve(M.probe(file)).then(function (r) {
            return (r && r.codecs && r.codecs[0]) ? String(r.codecs[0]) : '';
        });
    }).catch(function () { return ''; });
    if (_codecCache) _codecCache.set(file, p);
    return p;
}

/**
 * The lossless output format a tool should default to instead of `current`,
 * or null when nothing should change: the source is not an mp4-family file,
 * its codec is lossy or unknown, or `current` is already lossless.
 * Prefers FLAC (lossless and compact, like ALAC), then WAV, within `available`.
 */
function losslessUpgrade(file, available, current) {
    var ext = ((file && file.name) || '').split('.').pop().toLowerCase();
    if (!BOX_EXT[ext] || LOSSLESS_OUT[String(current || '').toLowerCase()]) return Promise.resolve(null);
    return sourceCodec(file).then(function (codec) {
        if (!LOSSLESS_CODECS[codec]) return null;
        var list = available && available.length ? available : ['flac', 'wav'];
        var to = list.indexOf('flac') >= 0 ? 'flac' : (list.indexOf('wav') >= 0 ? 'wav' : null);
        if (to && window.__aeFeature) {
            try { window.__aeFeature('lossless_source_default', { codec: codec, ext: ext, from: current || '', to: to }); } catch (_) {}
        }
        return to;
    });
}

function estimateBitrate(file, durationSec) {
    if (!file || !file.size || !durationSec || durationSec <= 0) return null;
    return Math.round((file.size * 8) / (durationSec * 1000));
}

function closestBitrate(target, bitrates) {
    if (!bitrates || !bitrates.length) return target;
    return bitrates.reduce(function(a, b) {
        return Math.abs(b - target) < Math.abs(a - target) ? b : a;
    }, bitrates[0]);
}

function recommend(file, durationSec, available, formatData) {
    var ext = (file.name || '').split('.').pop().toLowerCase();
    var format = mapFormat(ext, available);
    var estBitrate = estimateBitrate(file, durationSec);
    var bitrate = null;
    if (formatData && formatData[format] && !formatData[format].nb) {
        var options = formatData[format].b || [64, 128, 192, 256, 320];
        bitrate = closestBitrate(estBitrate || 192, options);
    }
    return { format: format, bitrate: bitrate };
}

/**
 * Detect number of channels from an AudioBuffer or number.
 * Stores the result so channel select can show the original value.
 */
var _detectedChannels = 0;
function detectChannels(bufferOrNum) {
    if (typeof bufferOrNum === 'number') {
        _detectedChannels = bufferOrNum;
    } else if (bufferOrNum && bufferOrNum.numberOfChannels) {
        _detectedChannels = bufferOrNum.numberOfChannels;
    }
    return _detectedChannels;
}
function getDetectedChannels() { return _detectedChannels; }

/* ── UI Helpers ── */

/**
 * Extract available formats from UI elements.
 * @param {string} selector - CSS selector for buttons/items, or a <select> element
 * @returns {string[]} list of format strings
 */
function getAvailable(selector) {
    if (!selector) return [];
    var el = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!el) return [];
    // <select> element
    if (el.tagName === 'SELECT') {
        var fmts = [];
        for (var i = 0; i < el.options.length; i++) {
            var v = el.options[i].value;
            if (v && v !== 'source') fmts.push(v);
        }
        return fmts;
    }
    // Buttons/dropdown items — querySelectorAll on parent or direct selector
    var items = document.querySelectorAll(selector);
    if (!items.length) return [];
    var result = [];
    items.forEach(function(btn) {
        var fmt = btn.getAttribute('data-fmt') || btn.getAttribute('data-format') || btn.value;
        if (fmt && fmt !== 'source') result.push(fmt);
    });
    return result;
}

/**
 * Select a format in buttons/dropdown items.
 * Auto-detects data-fmt vs data-format attribute.
 * @param {string} format - format to select
 * @param {string} selector - CSS selector for all format buttons/items
 * @returns {string} actually selected format (may differ if format not found)
 */
function selectButton(format, selector) {
    var items = document.querySelectorAll(selector);
    if (!items.length) return format;
    var found = false;
    items.forEach(function(btn) {
        var fmt = btn.getAttribute('data-fmt') || btn.getAttribute('data-format');
        var match = fmt === format;
        btn.classList.toggle('active', match);
        if (match) found = true;
    });
    if (!found && items.length) {
        items[0].classList.add('active');
        return items[0].getAttribute('data-fmt') || items[0].getAttribute('data-format') || format;
    }
    return format;
}

/**
 * Select a format in a <select> element.
 * @param {string} format - format to select
 * @param {HTMLSelectElement|string} selectEl - <select> element or CSS selector
 * @returns {string} actually selected value
 */
function selectOption(format, selectEl) {
    var el = typeof selectEl === 'string' ? document.querySelector(selectEl) : selectEl;
    if (!el) return format;
    for (var i = 0; i < el.options.length; i++) {
        if (el.options[i].value === format) {
            el.value = format;
            return format;
        }
    }
    return el.value; // keep current if not found
}

/**
 * Populate a <select> quality dropdown with bitrate options.
 * @param {HTMLSelectElement|string} selectEl - quality <select> or CSS selector
 * @param {HTMLElement|string|null} containerEl - row to show/hide (null to skip)
 * @param {Object} formatData - FORMAT_DATA object
 * @param {string} format - current output format
 * @param {number|null} targetBitrate - estimated bitrate to pre-select
 * @returns {number|null} selected bitrate, or null if lossless
 */
function populateQuality(selectEl, containerEl, formatData, format, targetBitrate) {
    var sel = typeof selectEl === 'string' ? document.querySelector(selectEl) : selectEl;
    var cont = containerEl ? (typeof containerEl === 'string' ? document.querySelector(containerEl) : containerEl) : null;
    if (!sel || !formatData) return null;
    var info = formatData[format];
    if (!info || info.nb) {
        if (cont) cont.style.display = 'none';
        return null;
    }
    if (cont) cont.style.display = '';
    var bitrates = info.b || [64, 128, 192, 256, 320];
    var best = closestBitrate(targetBitrate || 192, bitrates);
    sel.innerHTML = bitrates.map(function(b) {
        return '<option value="' + b + '"' + (b === best ? ' selected' : '') + '>' + b + ' kbps</option>';
    }).join('');
    // Auto-update channels when format changes
    var chSel = document.getElementById('fs-channel-select');
    if (chSel) populateChannels(chSel, format, null);
    return best;
}

/**
 * Move a format control from `shown` to a lossless format when the source is a
 * lossless recording in an mp4-family container (see losslessUpgrade).
 * Applied through the tool's OWN control — a click on the button, a change
 * event on the <select> — so the tool's variables follow; and only while the
 * control still shows `shown`, never over a user's pick or a restored draft.
 * opts: {buttons: selector} | {select: selector|element}, plus optional
 * {quality, qualityRow, formatData} for autoApply's quality row.
 */
function upgradeControl(file, opts, shown) {
    opts = opts || {};
    var available = [];
    if (opts.buttons) available = getAvailable(opts.buttons);
    else if (opts.select) available = getAvailable(typeof opts.select === 'string' ? document.querySelector(opts.select) : opts.select);
    return losslessUpgrade(file, available, shown).then(function (to) {
        if (!to || to === shown) return null;
        if (opts.buttons) {
            var act = document.querySelector(opts.buttons + '.active');
            var cur = act && (act.getAttribute('data-fmt') || act.getAttribute('data-format'));
            if (cur !== shown) return null;
            var btns = document.querySelectorAll(opts.buttons), hit = null;
            for (var i = 0; i < btns.length; i++) {
                if ((btns[i].getAttribute('data-fmt') || btns[i].getAttribute('data-format')) === to) { hit = btns[i]; break; }
            }
            if (!hit) return null;
            hit.click();
        } else if (opts.select) {
            var sel = typeof opts.select === 'string' ? document.querySelector(opts.select) : opts.select;
            if (!sel || sel.value !== shown) return null;
            if (selectOption(to, sel) !== to) return null;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
            return null;
        }
        if (opts.quality && opts.formatData) populateQuality(opts.quality, opts.qualityRow || null, opts.formatData, to, null);
        return to;
    }).catch(function () { return null; });
}

/**
 * All-in-one: detect format + estimate bitrate + apply to UI.
 * Works with buttons (data-fmt), dropdown items (data-format), and <select>.
 *
 * @param {File} file - uploaded file
 * @param {number} durationSec - decoded audio duration in seconds
 * @param {Object} opts
 * @param {string} [opts.buttons] - CSS selector for format buttons (data-fmt or data-format)
 * @param {string|HTMLSelectElement} [opts.select] - CSS selector or element for format <select>
 * @param {string|HTMLSelectElement} [opts.quality] - CSS selector or element for quality <select>
 * @param {string|HTMLElement} [opts.qualityRow] - CSS selector or element for quality container
 * @param {Object} [opts.formatData] - FORMAT_DATA from config
 * @returns {{format:string, bitrate:number|null}}
 */
function autoApply(file, durationSec, opts) {
    if (!opts) opts = {};

    // Detect channels from AudioBuffer if provided
    if (opts.audioBuffer) detectChannels(opts.audioBuffer);
    else if (opts.channels_count) detectChannels(opts.channels_count);

    // 1. Get available formats from UI
    var available = [];
    if (opts.buttons) {
        available = getAvailable(opts.buttons);
    } else if (opts.select) {
        var selEl = typeof opts.select === 'string' ? document.querySelector(opts.select) : opts.select;
        available = getAvailable(selEl);
    }

    // 2. Recommend format + bitrate
    var rec = recommend(file, durationSec, available, opts.formatData || null);

    // 2b. Track exotic format mapping
    var ext = (file.name || '').split('.').pop().toLowerCase();
    if (ext !== rec.format && window.__aeFeature) {
        window.__aeFeature('format_smart', {from: ext, to: rec.format, br: rec.bitrate});
    }

    // 3. Apply format to UI
    if (opts.buttons) {
        rec.format = selectButton(rec.format, opts.buttons);
    } else if (opts.select) {
        rec.format = selectOption(rec.format, opts.select);
    }

    // 4. Apply quality to UI (if provided)
    if (opts.quality && opts.formatData) {
        populateQuality(opts.quality, opts.qualityRow || null, opts.formatData, rec.format, rec.bitrate);
    }

    // 5. Apply channels to UI (auto-create if quality select exists but no channel select)
    if (opts.channels) {
        populateChannels(opts.channels, rec.format, null);
    } else if (opts.quality) {
        // Auto-create channel select next to quality select
        var qEl = typeof opts.quality === 'string' ? document.querySelector(opts.quality) : opts.quality;
        populateChannels(null, rec.format, qEl);
    }

    // 6. A lossless source in an mp4-family container: move the default to a
    //    lossless format once the header has been read (upgradeControl).
    upgradeControl(file, opts, rec.format);

    return rec;
}

// Formats that support max 2 channels (mono/stereo only)
var MAX_STEREO = {mp3:1,amr:1,gsm:1,g722:1};

/**
 * Get channel options for a format.
 * @param {string} format
 * @returns {Array<{value:string, label:string}>}
 */
function getChannelOptions(format) {
    var L = window.__LOCALE || 'en';
    var monoLabel = {en:'Mono',ru:'Моно',ko:'모노',ar:'أحادي',id:'Mono',pt:'Mono',de:'Mono',es:'Mono',fr:'Mono', uk: 'Моно', tr: 'Mono'}[L] || 'Mono';
    var stereoLabel = {en:'Stereo',ru:'Стерео',ko:'스테레오',ar:'ستيريو',id:'Stereo',pt:'Estéreo',de:'Stereo',es:'Estéreo',fr:'Stéréo', uk: 'Стерео', tr: 'Stereo'}[L] || 'Stereo';
    var opts = [
        {value:'1', label:monoLabel},
        {value:'2', label:stereoLabel}
    ];
    if (!MAX_STEREO[format]) {
        opts.push({value:'6', label:'5.1'});
        opts.push({value:'8', label:'7.1'});
    }
    return opts;
}

/**
 * Populate or create a channel <select>.
 * If selectEl is null and qualityRow exists, creates the select automatically.
 * @param {HTMLSelectElement|string|null} selectEl - existing channel <select>, or null to auto-create
 * @param {string} format - current output format
 * @param {HTMLElement|null} anchorEl - element to insert channel select after (for auto-create)
 * @returns {string} current channel value
 */
function _getToolSlug() {
    var mt = document.querySelector('meta[name="tool-slug"]');
    return mt ? mt.content : null;
}

function populateChannels(selectEl, format, anchorEl) {
    var sel = selectEl ? (typeof selectEl === 'string' ? document.querySelector(selectEl) : selectEl) : null;

    // Auto-create if not exists and anchor provided
    if (!sel && anchorEl) {
        var anchor = typeof anchorEl === 'string' ? document.querySelector(anchorEl) : anchorEl;
        if (anchor) {
            var existing = document.getElementById('fs-channel-select');
            if (existing) {
                sel = existing;
            } else {
                // Insert channel select after quality — mirror the quality wrapper structure
                var newSel = document.createElement('select');
                newSel.id = 'fs-channel-select';
                newSel.style.cssText = 'width:auto;min-width:fit-content';
                newSel.setAttribute('aria-label', 'Channels');

                // Find wrapper around quality select for consistent styling.
                // Tools that wrap each select in its own labeled column should be
                // listed here so we clone the structure (keeps label-on-top alignment).
                var qWrap = anchor.closest('.quality-group,.sp-export-select-wrap,.export-row,.save-row,.vf-row,.cs-export-group');
                if (qWrap && qWrap.parentElement) {
                    // Clone wrapper structure (shallow — keeps the wrapper's classes/styles)
                    var chWrap = qWrap.cloneNode(false);
                    chWrap.id = 'fs-channel-group';
                    chWrap.innerHTML = '';
                    // If the source wrapper had a <label>, mirror it for the channel select
                    // so the new column visually pairs with the others.
                    var origLabel = qWrap.querySelector('label');
                    if (origLabel) {
                        var newLabel = document.createElement('label');
                        newLabel.className = origLabel.className || '';
                        var L = window.__LOCALE || 'en';
                        newLabel.textContent = ({en:'Channels',ru:'Каналы',ko:'채널',ar:'القنوات',id:'Saluran',pt:'Canais',de:'Kanäle',es:'Canales',fr:'Canaux',uk:'Канали',tr:'Kanallar'}[L]) || 'Channels';
                        chWrap.appendChild(newLabel);
                    }
                    newSel.className = anchor.className || '';
                    chWrap.appendChild(newSel);
                    qWrap.parentElement.insertBefore(chWrap, qWrap.nextSibling);
                } else {
                    // No known wrapper — insert after anchor
                    newSel.className = anchor.className || 'quality-select';
                    newSel.style.cssText = 'width:auto;min-width:fit-content';
                    if (anchor.nextSibling) anchor.parentElement.insertBefore(newSel, anchor.nextSibling);
                    else anchor.parentElement.appendChild(newSel);
                }
                sel = document.getElementById('fs-channel-select');
                // Bind to per-tool settings
                var slug = _getToolSlug();
                if (slug && window.__toolSettings) {
                    window.__toolSettings.bind(slug, sel, 'channels', 'auto');
                }
                // Track manual user changes
                sel.addEventListener('change', function() {
                    sel.dataset.manual = '1';
                    if (window.__ae) window.__ae('feature_use', {slug: slug || '', action: 'channel_change', value: sel.value});
                });
            }
        }
    }

    if (!sel) return 'auto';

    var options = getChannelOptions(format);
    // Determine best channel value:
    // 1. If format supports detected channels → use detected (preserve original)
    // 2. If user manually changed → keep their choice (if format supports it)
    // 3. If format doesn't support current → clamp to max supported
    var detStr = _detectedChannels > 0 ? String(_detectedChannels) : '2';
    var current = sel.value;
    var isManual = sel.dataset.manual === '1';

    if (!current || current === 'auto') {
        current = detStr;
    } else if (!isManual && options.some(function(o) { return o.value === detStr; })) {
        // Format changed and supports original → restore original
        current = detStr;
    }

    sel.innerHTML = options.map(function(o) {
        return '<option value="' + o.value + '"' + (o.value === current ? ' selected' : '') + '>' + o.label + '</option>';
    }).join('');
    if (!options.some(function(o) { return o.value === current; })) {
        // Current not available in this format → clamp to max supported
        var maxVal = options[options.length - 1].value;
        sel.value = maxVal;
    }
    // Restore per-tool saved value after rebuilding options
    var slug = _getToolSlug();
    if (slug && window.__toolSettings) {
        var saved = window.__toolSettings.load(slug, 'channels', '');
        if (saved && saved !== 'auto' && options.some(function(o) { return o.value === saved; })) {
            sel.value = saved;
        }
    }
    return sel.value;
}

/**
 * Get the current channel selection value.
 * Checks both explicit channel selects and auto-created ones.
 * @returns {string} 'auto', '1', '2', '6', or '8'
 */
function getChannels() {
    var sel = document.getElementById('fs-channel-select') || document.getElementById('channelSelect');
    if (!sel) return '';
    var v = sel.value;
    return v === 'auto' ? '' : v;
}

// Auto-detect channels: patch decodeAudioData to capture channel count
(function() {
    // `typeof X !== 'undefined'` accepts null: a hardened WebView (Samsung S938B,
    // Chrome WebView 152 — Sentry M8, 27 events) exposes `AudioContext = null`,
    // and `.prototype` of null threw at page load. A constructor is a function.
    var AC = typeof AudioContext === 'function' ? AudioContext
           : (typeof webkitAudioContext === 'function' ? webkitAudioContext : null);
    if (!AC || !AC.prototype) return;
    var _origDecode = AC.prototype.decodeAudioData;
    if (!_origDecode) return;
    AC.prototype.decodeAudioData = function() {
        var p = _origDecode.apply(this, arguments);
        if (p && typeof p.then === 'function') {
            p.then(function(buf) {
                if (buf && buf.numberOfChannels && _detectedChannels === 0) {
                    detectChannels(buf);
                    // Update channel select label if already created
                    var chSel = document.getElementById('fs-channel-select');
                    if (chSel) {
                        var fmt = 'mp3';
                        var activeBtn = document.querySelector('.format-option.active, [data-fmt].active');
                        if (activeBtn) fmt = activeBtn.getAttribute('data-format') || activeBtn.getAttribute('data-fmt') || 'mp3';
                        populateChannels(chSel, fmt, null);
                    }
                }
            }).catch(function() {});
        }
        return p;
    };
    // Also patch webkitAudioContext if exists and different from AC
    if (window.webkitAudioContext && webkitAudioContext !== AC && webkitAudioContext.prototype.decodeAudioData) {
        webkitAudioContext.prototype.decodeAudioData = AC.prototype.decodeAudioData;
    }
})();

// Auto-init: add channel select to any tool page that has a quality/bitrate dropdown
// Runs after DOM is ready, no manual integration needed per-tool
function autoInitChannels() {
    // Don't create if already exists
    if (document.getElementById('fs-channel-select') || document.getElementById('channelSelect')) return;
    // OPT-IN ONLY (root fix 2026-06-09). This used to auto-inject an audio
    // Mono/Stereo channel <select> next to ANY select[id*="Quality"|"bitrate"] /
    // .quality-select, which FALSE-MATCHED non-audio tools (barcode export
    // RESOLUTION, image quality, video→GIF, etc.) and rendered a stray
    // "Стерео/Моно" control where channels are meaningless. Real, working channel
    // selection is the ChannelModule + components.channel-select component
    // (#channelSelect) — that path is untouched (the guard above skips when it
    // exists). Nothing reads this auto-injected select except optional telemetry
    // (app.blade.php __aeToolComplete d.channels), so requiring an explicit opt-in
    // is safe and breaks no export. A tool that genuinely wants the auto channel
    // select (without the full component) adds data-fs-channels="1" to the page.
    // ACX-mastering is intentionally mono-only and must NOT get a channel select.
    if (!document.querySelector('[data-fs-channels]')) return;
    // Find a quality/bitrate select element to anchor channel select next to
    // Only works with <select> elements — custom dropdowns handled per-tool
    var anchor = document.querySelector('.quality-select') ||
                 document.querySelector('select[id*="Quality" i]') ||
                 document.querySelector('select[id*="bitrate" i]');
    if (!anchor) return;
    // Per-tool opt-out: tools where channel selection is not meaningful for the
    // export action (e.g. chapter-splitter — channels apply per file but the
    // primary action is splitting). Add data-fs-no-channels="1" to skip.
    if (anchor.hasAttribute('data-fs-no-channels')) return;
    // Only on tool pages (have meta tool-slug)
    if (!document.querySelector('meta[name="tool-slug"]')) return;
    // Detect current format from active format button/option
    var fmt = 'mp3';
    var activeBtn = document.querySelector('.format-option.active, [data-fmt].active, [data-format].active');
    if (activeBtn) fmt = activeBtn.getAttribute('data-format') || activeBtn.getAttribute('data-fmt') || 'mp3';
    var fmtSelect = document.querySelector('#format-select, #spExportFormat, [id*="FmtSelect"]');
    if (fmtSelect) fmt = fmtSelect.value || fmt;
    populateChannels(null, fmt, anchor);

    // Listen for format changes to update channel options
    document.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-format], [data-fmt]');
        if (!btn) return;
        var newFmt = btn.getAttribute('data-format') || btn.getAttribute('data-fmt');
        if (newFmt && newFmt !== 'source') {
            var chSel = document.getElementById('fs-channel-select');
            if (chSel) populateChannels(chSel, newFmt, null);
        }
    }, true);
    document.addEventListener('change', function(e) {
        var sel = e.target;
        if (sel.id && (/format/i.test(sel.id) || /fmt/i.test(sel.id)) && sel.tagName === 'SELECT') {
            var newFmt = sel.value;
            if (newFmt && newFmt !== 'source') {
                var chSel = document.getElementById('fs-channel-select');
                if (chSel) populateChannels(chSel, newFmt, null);
            }
        }
    }, true);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(autoInitChannels, 500); });
} else {
    setTimeout(autoInitChannels, 500);
}

window.FormatSmart = {
    FORMAT_MAP: FORMAT_MAP,
    mapFormat: mapFormat,
    estimateBitrate: estimateBitrate,
    closestBitrate: closestBitrate,
    recommend: recommend,
    getAvailable: getAvailable,
    selectButton: selectButton,
    selectOption: selectOption,
    populateQuality: populateQuality,
    populateChannels: populateChannels,
    getChannels: getChannels,
    getChannelOptions: getChannelOptions,
    detectChannels: detectChannels,
    getDetectedChannels: getDetectedChannels,
    autoApply: autoApply,
    sourceCodec: sourceCodec,
    losslessUpgrade: losslessUpgrade,
    upgradeControl: upgradeControl
};
})();
